/* Push Analytics widget — Update #93 */
import React, { useEffect, useState } from "react";
import { TrendingUp, CheckCircle2, Clock, Mic, BarChart3, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import s from "./PushAnalytics.module.css";

const RANGES = [
  { id: "24h", label: "24h" },
  { id: "7d",  label: "7d" },
  { id: "30d", label: "30d" },
];

function fmtMs(ms) {
  if (!ms || ms < 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export default function PushAnalytics() {
  const [windowKey, setWindowKey] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/push-to-air/analytics?window=${windowKey}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [windowKey]);

  const empty = !data || (data.kpis?.total ?? 0) === 0;
  const kpi = data?.kpis || {};
  const series = data?.timeseries || [];
  const sparkMax = Math.max(1, ...series.map((d) => d.ok + d.fail));
  const providers = data?.by_provider || [];

  return (
    <div className={s.widget} data-testid="push-analytics">
      <div className={s.header}>
        <div className={s.title}>
          <BarChart3 size={13} /> Push analytics
        </div>
        <div className={s.rangePicker}>
          {RANGES.map((r) => (
            <button type="button" key={r.id}
              onClick={() => setWindowKey(r.id)}
              className={`${s.rangePill} ${windowKey === r.id ? s.rangePillActive : ""}`}
              data-testid={`pa-range-${r.id}`}>{r.label}</button>
          ))}
          {loading && <Loader2 size={11} className={s.spin} />}
        </div>
      </div>

      {empty ? (
        <div className={s.emptyState} data-testid="pa-empty">
          No pushes in this window yet. Generate a drop in Artist Drops and hit <b>Push to Air</b>.
        </div>
      ) : (
        <>
          <div className={s.kpis} data-testid="pa-kpis">
            <Kpi icon={TrendingUp}  label="Total pushes" value={kpi.total} testid="pa-kpi-total" />
            <Kpi icon={CheckCircle2} label="Success rate" value={`${(kpi.success_rate ?? 0).toFixed(0)}%`}
                 color={kpi.success_rate >= 95 ? "#3ecf8e" : kpi.success_rate >= 75 ? "#f59e0b" : "#ff5a7a"}
                 testid="pa-kpi-success" />
            <Kpi icon={Clock} label="p95 latency" value={fmtMs(kpi.p95_latency_ms)} testid="pa-kpi-latency" />
            <Kpi icon={Mic} label="Top voice" value={kpi.top_voice || "—"} subtle testid="pa-kpi-voice" />
          </div>

          <div className={s.row}>
            {/* Per-provider */}
            <div className={s.col}>
              <div className={s.colTitle}>By provider</div>
              {providers.length === 0 ? (
                <div className={s.subEmpty}>No data</div>
              ) : providers.map((p) => (
                <div key={p.provider} className={s.providerRow} data-testid={`pa-provider-${p.provider}`}>
                  <div className={s.providerLabel}>{p.provider}</div>
                  <div className={s.bar}>
                    <div className={s.barOk}    style={{ flex: p.ok }} />
                    <div className={s.barFail}  style={{ flex: p.fail }} />
                  </div>
                  <div className={s.providerStats}>
                    {p.total} · {p.success_rate.toFixed(0)}% · {fmtMs(p.p95_ms)}
                  </div>
                </div>
              ))}
            </div>

            {/* Top voices */}
            <div className={s.col}>
              <div className={s.colTitle}>Top voices</div>
              {(data?.top_voices || []).length === 0 ? (
                <div className={s.subEmpty}>No data</div>
              ) : (data?.top_voices || []).map((v, i) => (
                <div key={`${v.name}-${i}`} className={s.listRow} data-testid={`pa-voice-${i}`}>
                  <div className={s.listRank}>#{i + 1}</div>
                  <div className={s.listName}>{v.name}</div>
                  <div className={s.listCount}>{v.total}</div>
                </div>
              ))}
            </div>

            {/* Top drop types */}
            <div className={s.col}>
              <div className={s.colTitle}>Top drop types</div>
              {(data?.top_drop_types || []).length === 0 ? (
                <div className={s.subEmpty}>No data</div>
              ) : (data?.top_drop_types || []).map((d, i) => (
                <div key={`${d.name}-${i}`} className={s.listRow} data-testid={`pa-droptype-${i}`}>
                  <div className={s.listRank}>#{i + 1}</div>
                  <div className={s.listName}>{d.name}</div>
                  <div className={s.listCount}>{d.total}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Sparkline trend */}
          {series.length > 0 && (
            <div className={s.sparklineWrap}>
              <div className={s.colTitle}>Trend</div>
              <div className={s.sparkline} data-testid="pa-sparkline">
                {series.map((d) => {
                  const okPct   = (d.ok   / sparkMax) * 100;
                  const failPct = (d.fail / sparkMax) * 100;
                  return (
                    <div key={d.date} className={s.sparkCol} title={`${d.date}: ${d.ok} ok / ${d.fail} fail`}>
                      <div className={s.sparkFail} style={{ height: `${failPct}%` }} />
                      <div className={s.sparkOk}   style={{ height: `${okPct}%` }} />
                    </div>
                  );
                })}
              </div>
              <div className={s.sparkLegend}>
                <span><span className={s.legendDot} style={{ background: "#3ecf8e" }} /> ok</span>
                <span><span className={s.legendDot} style={{ background: "#ff5a7a" }} /> fail</span>
                <span style={{ marginLeft: "auto", opacity: 0.6 }}>{series[0]?.date} — {series[series.length - 1]?.date}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color, subtle, testid }) {
  return (
    <div className={s.kpiCard} data-testid={testid}>
      <div className={s.kpiTop}>
        <Icon size={11} color={color || "#4a9eff"} />
        <span className={s.kpiLabel}>{label}</span>
      </div>
      <div className={s.kpiValue} style={{ color: subtle ? "#c8ccd8" : (color || "#c8ccd8") }}>{value ?? "—"}</div>
    </div>
  );
}
